<template>
  <div class="c-datasource-settings-modal">
    <BittsModal
      :width="1400"
      sidebar-title="Settings"
      :title="data.title"
      variant="sidebar"
      :loading="loading"
      :use-mask-to-close="true"
      :hide-footer="hideFooter"
      :show-primary-button="showPrimaryButton"
      :show-secondary-button="showSecondaryButton"
      :container-class="containerClass"
      @closed="close"
      @action="handleModalAction"
      :disabled="modalActionDisabled"
      :action-loading="modalActionLoading"
      :action-text="modalActionText"
      :no-padding="true"
    >
      <template #sidebar-header-prefix>
        <BittsSvg
          v-if="feed?.integration"
          class="w-24 h-24 mr-16"
          :svg="feed.integration.type + 'Icon'"
        />
      </template>
      <template #sidebar-footer>
        <BittsButton
          :disabled="saving || sourceDeleting || isExternalFileDataFeed"
          type="danger"
          text="Remove data source"
          variant="ghost"
          :left-icon="['fak', 'delete']"
          data-testid="c-datasource-settings-delete"
          @click="showDeleteModal"
        />
      </template>
      <template #sidebar-content>
        <ModalSidebarLink
          v-for="(item, i) in ModalSidebarItems"
          route-name="data-sources-settings-new"
          :item="item"
          :key="item.tab"
          :class="{
            'mt-auto': i === ModalSidebarItems.length - 1,
          }"
        />
      </template>
      <template v-if="isFieldPresetChildRoute" #header>
        <div class="flex p-[27.5px] gap-8">
          <span class="text-neutral-text-weak font-bold text-lg">
            Sharing presets /
            <span class="text-neutral-text-strong">
              {{ presetEditorHeaderName }}
            </span>
          </span>
          <BittsTag
            v-if="presetEditorFieldCount !== null"
            :text="`${presetEditorFieldCount} ${presetEditorFieldCount === 1 ? 'field' : 'fields'}`"
            color="secondary"
            size="small"
          />
        </div>
      </template>
      <template
        v-if="
          tab === DataSourceSettingsTabType.FIELD_PRESETS ||
          tab === DataSourceSettingsTabType.PARTNER_MAPPING ||
          tab === DataSourceSettingsTabType.PRODUCT_MAPPING
        "
        #footer
      >
        <template v-if="tab === DataSourceSettingsTabType.FIELD_PRESETS">
          <div v-if="!isFieldPresetChildRoute" class="flex justify-end w-full">
            <BittsButton
              text="Close"
              type="neutral"
              size="large"
              @click="close"
            />
          </div>

          <div v-else class="flex justify-between w-full">
            <BittsTooltip
              v-if="presetEditorFooterData.showRestoreButton"
              placement="bottomLeft"
            >
              <BittsButton
                text="Restore recommended fields"
                type="neutral"
                variant="outline"
                size="large"
                :left-icon="['fas', 'arrow-rotate-left']"
                @click="handleRestoreDefault"
                :aria-disabled="presetEditorFooterData.isRestoreDisabled"
              />
              <template #title>
                Revert this preset to Crossbeam’s recommended field selection.
              </template>
            </BittsTooltip>
            <div class="flex gap-8 ml-auto">
              <BittsButton
                :text="isCreatingPreset ? 'Cancel' : 'Back'"
                type="neutral"
                variant="outline"
                size="large"
                @click="handleCancelPresetEdit"
              />
              <BittsButton
                :text="presetEditorFooterData.buttonText"
                type="primary"
                size="large"
                @click="handlePresetAction"
                :disabled="presetEditorFooterData.isDisabled"
                :loading="presetEditorFooterData.isLoading"
              />
            </div>
          </div>
        </template>
        <template v-else-if="tab === DataSourceSettingsTabType.PARTNER_MAPPING">
          <div class="flex justify-end gap-8 w-full">
            <BittsButton
              text="Close"
              type="neutral"
              size="large"
              @click="close"
            />
            <BittsButton
              text="Save"
              type="primary"
              size="large"
              @click="handleModalAction"
              :disabled="modalActionDisabled"
              :loading="modalActionLoading"
            />
          </div>
        </template>
        <template v-else-if="tab === DataSourceSettingsTabType.PRODUCT_MAPPING">
          <div class="flex justify-end gap-8 w-full">
            <BittsButton
              text="Close"
              type="neutral"
              size="large"
              @click="close"
            />
            <BittsTooltip placement="topRight">
              <BittsButton
                text="Save"
                type="primary"
                size="large"
                @click="handleModalAction"
                :aria-disabled="modalActionDisabled"
                :loading="modalActionLoading"
              />
              <template v-if="modalActionDisabled" #title>
                {{ productMappingSaveReason }}
              </template>
            </BittsTooltip>
          </div>
        </template>
      </template>
      <div class="c-datasource-settings-modal__content">
        <RouterView
          v-if="isFieldPresetChildRoute"
          ref="presetEditorRef"
          :feed-id="feedId"
          :source-id="sourceId"
          :trigger-action="triggerAction"
          :trigger-restore="triggerRestore"
          @footer-data="handlePresetEditorFooterData"
          @field-count-change="handlePresetEditorFieldCount"
          @preset-name-change="handlePresetEditorNameChange"
          @action-complete="triggerAction = 0"
          @restore-complete="triggerRestore = 0"
          @delete-preset-requested="handleDeletePreset"
        />
        <component
          v-else
          :is="data.TabComponent"
          ref="tabComponentRef"
          :class="hideFooter ? 'min-h-[660px]' : 'min-h-[600px]'"
          :feed-id="feedId"
          :source-id="isPartnerMappingTab ? (source?.id ?? sourceId) : sourceId"
          @delete-preset-requested="handleDeletePreset"
        />
      </div>
    </BittsModal>

    <RouterViewWrapper v-if="!isFieldPresetChildRoute" />
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsModal,
  BittsSvg,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { DataSourceSettingsTabType } from '@/components/data-sources/SettingsModal/constants';
import { DataImportTab } from '@/components/data-sources/SettingsModal/DataImportTab';
import { FieldMappingTab } from '@/components/data-sources/SettingsModal/FieldMappingTab';
import FieldPresetsTab from '@/components/data-sources/SettingsModal/FieldPresetsTab/components/FieldPresetsTab.vue';
import GeneralTab from '@/components/data-sources/SettingsModal/GeneralTab/components/GeneralTab.vue';
import { PartnerMappingTab } from '@/components/data-sources/SettingsModal/PartnerMappingTab';
import { usePartnerMappingEligibility } from '@/components/data-sources/SettingsModal/PartnerMappingTab/composables/usePartnerMappingEligibility';
import { ProductMappingTab } from '@/components/data-sources/SettingsModal/ProductMappingTab';
import SharingTab from '@/components/data-sources/SettingsModal/SharingTab.vue';
import ModalSidebarLink, {
  ModalSidebarItem,
} from '@/components/ModalSidebarLink.vue';

import useDataSourceSettings from '@/composables/useDataSourceSettings';
import { useFieldPresets } from '@/composables/useFieldPresets';
import { SALESFORCE_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useFeatureFlagStore, useFeedsStore } from '@/stores';

type Props = {
  feedId: number;
  sourceId?: number;
};
const { feedId, sourceId } = defineProps<Props>();

const router = useRouter();
const {
  handlePatchFeed,
  handleSaveFieldMappingSettings,
  loading,
  saving,
  feed,
  source,
  sourceDeleting,
  isCSVFeed,
  isGSheetFeed,
  isDellSpilFeed,
  isExternalFileDataFeed,
  isAwsPartnerCentralFeed,
  isPreferredCRMFeed,
} = useDataSourceSettings();
const route = useRoute();

const { deletePreset } = useFieldPresets({
  feedId,
  sourceId,
});

const feedsStore = useFeedsStore();

const { isEligibleForPartnerMapping } = usePartnerMappingEligibility(
  () => feedId,
);

const { hasFeatureFlag } = useFeatureFlagStore();
const showPartnerMappingTab = computed(
  () =>
    hasFeatureFlag(FEATURE_FLAG.TEMP_PARTNER_MAPPING_FE) &&
    isEligibleForPartnerMapping.value &&
    Boolean(source.value?.id),
);
const showProductMappingTab = computed(
  () =>
    hasFeatureFlag('temp_account_products_sync') &&
    feed.value?.integration?.type === SALESFORCE_DATA_SOURCE_TYPE,
);
const isCRMFeed = computed(
  () =>
    !isCSVFeed.value && !isGSheetFeed.value && !isExternalFileDataFeed.value,
);
const hasMultipleCRMs = computed(() => feedsStore.hasMultipleCRMs);

const tab = computed(() => route.params.tab as DataSourceSettingsTabType);

const isFieldPresetChildRoute = computed(
  () => route.name === 'field-preset-new' || route.name === 'field-preset-edit',
);
const isFieldMappingTab = computed(
  () => tab.value === DataSourceSettingsTabType.FIELD_MAPPING,
);
const isPartnerMappingTab = computed(
  () => tab.value === DataSourceSettingsTabType.PARTNER_MAPPING,
);
const isProductMappingTab = computed(
  () => tab.value === DataSourceSettingsTabType.PRODUCT_MAPPING,
);
type SettingsTabInstance = {
  hasPendingChanges?: boolean;
  isSaving?: boolean;
  handleSaveAll?: () => void | Promise<void>;
  haveSettingsChanged?: boolean;
  disabledSaveReason?: string;
};
const tabComponentRef = ref<SettingsTabInstance | null>(null);

const presetEditorRef = ref();
const presetEditorFieldCount = ref<number | null>(null);
const presetEditorName = ref<string>('');
const presetEditorFooterData = ref({
  showRestoreButton: false,
  isRestoreDisabled: false,
  buttonText: 'Save',
  isDisabled: false,
  isLoading: false,
});

const triggerAction = ref(0);
const triggerRestore = ref(0);

const isCreatingPreset = computed(() => route.name === 'field-preset-new');
const presetEditorHeaderName = computed(() => {
  if (isCreatingPreset.value) {
    return 'Create Sharing Preset';
  }
  return presetEditorName.value || 'Edit Preset';
});

const hideFooter = computed(() => {
  if (
    [
      DataSourceSettingsTabType.GENERAL,
      DataSourceSettingsTabType.FIELD_MAPPING,
      DataSourceSettingsTabType.PARTNER_MAPPING,
      DataSourceSettingsTabType.PRODUCT_MAPPING,
    ].includes(tab.value)
  ) {
    return false;
  }

  if (tab.value === DataSourceSettingsTabType.FIELD_PRESETS) {
    return false;
  }

  return true;
});

const data = computed(() => getData(tab.value));

const disabledFieldMappingSave = computed(
  () =>
    isFieldMappingTab.value &&
    ((hasMultipleCRMs.value && !isPreferredCRMFeed.value) ||
      !tabComponentRef.value?.haveSettingsChanged),
);
const disabledPartnerMappingSave = computed(
  () => isPartnerMappingTab.value && !tabComponentRef.value?.hasPendingChanges,
);
const disabledProductMappingSave = computed(
  () => isProductMappingTab.value && !tabComponentRef.value?.hasPendingChanges,
);
const productMappingSaveReason = computed(
  () =>
    tabComponentRef.value?.disabledSaveReason ??
    'Select your CRM fields before saving',
);
const disabled = computed(
  () =>
    saving.value ||
    loading.value ||
    disabledFieldMappingSave.value ||
    disabledPartnerMappingSave.value ||
    disabledProductMappingSave.value,
);

const showPrimaryButton = computed(() => {
  if (tab.value === DataSourceSettingsTabType.FIELD_PRESETS) {
    return false;
  }
  if (
    tab.value === DataSourceSettingsTabType.PARTNER_MAPPING ||
    tab.value === DataSourceSettingsTabType.PRODUCT_MAPPING
  ) {
    return false;
  }
  return true;
});

const showSecondaryButton = computed(() => {
  if (tab.value === DataSourceSettingsTabType.FIELD_PRESETS) {
    return false;
  }
  if (
    tab.value === DataSourceSettingsTabType.PARTNER_MAPPING ||
    tab.value === DataSourceSettingsTabType.PRODUCT_MAPPING
  ) {
    return false;
  }
  return true;
});

const modalActionDisabled = computed(() => disabled.value);
const modalActionLoading = computed(() => {
  if (isPartnerMappingTab.value || isProductMappingTab.value) {
    return tabComponentRef.value?.isSaving ?? false;
  }
  return saving.value;
});
const modalActionText = computed(() => undefined);

const containerClass = computed(() => {
  if (tab.value === DataSourceSettingsTabType.FIELD_PRESETS) {
    return 'c-datasource-settings-modal__field-presets-footer';
  }
  if (tab.value === DataSourceSettingsTabType.PARTNER_MAPPING) {
    return 'c-datasource-settings-modal__partner-mapping-footer';
  }
  if (tab.value === DataSourceSettingsTabType.PRODUCT_MAPPING) {
    return 'c-datasource-settings-modal__product-mapping-footer';
  }
  return '';
});

watch(
  () => isFieldPresetChildRoute.value,
  (isChildRoute) => {
    if (!isChildRoute) {
      presetEditorFieldCount.value = null;
      presetEditorName.value = '';
      presetEditorFooterData.value = {
        showRestoreButton: false,
        isRestoreDisabled: false,
        buttonText: 'Save',
        isDisabled: false,
        isLoading: false,
      };
    }
  },
);

async function handleModalAction() {
  if (isFieldMappingTab.value) {
    await handleSaveFieldMappingSettings();
  } else if (isPartnerMappingTab.value || isProductMappingTab.value) {
    await tabComponentRef.value?.handleSaveAll?.();
  } else {
    await handlePatchFeed();
  }
}

function handlePresetEditorFooterData(
  data: typeof presetEditorFooterData.value,
) {
  presetEditorFooterData.value = data;
}

function handlePresetEditorFieldCount(count: number) {
  presetEditorFieldCount.value = count;
}

function handlePresetEditorNameChange(name: string) {
  presetEditorName.value = name;
}

function handlePresetAction() {
  triggerAction.value++;
}

function handleRestoreDefault() {
  triggerRestore.value++;
}

function handleCancelPresetEdit() {
  const { id, tab, source_id } = route.params;
  router.push({
    name: 'data-sources-settings-new',
    params: { id, tab, source_id },
  });
}

async function handleDeletePreset(preset: { id: number; name: string }) {
  await deletePreset(preset.id, preset.name);

  if (isFieldPresetChildRoute.value) {
    handleCancelPresetEdit();
  }
}

async function close() {
  if (route.query.from === 'onboarding') {
    await router.push({ name: 'onboarding' });
  } else {
    await router.push({ name: 'data-sources' });
  }
}

async function showDeleteModal() {
  await router.push({ name: 'delete-data-source' });
}

const tabComponents = computed(() => ({
  [DataSourceSettingsTabType.GENERAL]: GeneralTab,
  [DataSourceSettingsTabType.SHARING]: SharingTab,
  [DataSourceSettingsTabType.FIELD_PRESETS]: FieldPresetsTab,
  [DataSourceSettingsTabType.DATA_IMPORT]: DataImportTab,
  [DataSourceSettingsTabType.FIELD_MAPPING]: FieldMappingTab,
  [DataSourceSettingsTabType.PARTNER_MAPPING]: PartnerMappingTab,
  [DataSourceSettingsTabType.PRODUCT_MAPPING]: ProductMappingTab,
}));

function getData(tab: DataSourceSettingsTabType) {
  const item = ModalSidebarItems.value.find((i) => i.tab === tab);
  const title = item?.label || 'General';
  const TabComponent = item ? tabComponents.value[item.tab] : GeneralTab;
  return { title, TabComponent };
}
const ModalSidebarItems = computed<
  ModalSidebarItem<DataSourceSettingsTabType>[]
>(
  () =>
    [
      {
        label: 'General',
        icon: ['fak', 'settings'],
        tab: DataSourceSettingsTabType.GENERAL,
      },
      isCRMFeed.value &&
        !isDellSpilFeed.value && {
          label: 'Field Sync',
          icon: ['fak', 'refresh'],
          tab: DataSourceSettingsTabType.DATA_IMPORT,
        },
      !isAwsPartnerCentralFeed.value && {
        label: 'Field Presets',
        icon: ['fak', 'slider'],
        tab: DataSourceSettingsTabType.FIELD_PRESETS,
      },
      isCRMFeed.value &&
        !isAwsPartnerCentralFeed.value && {
          label: 'Field Mapping',
          icon: ['fak', 'mapping'],
          tab: DataSourceSettingsTabType.FIELD_MAPPING,
        },
      showPartnerMappingTab.value &&
        !isAwsPartnerCentralFeed.value && {
          label: 'Partner Mapping',
          icon: ['fak', 'partner-mapping'],
          tab: DataSourceSettingsTabType.PARTNER_MAPPING,
        },
      showProductMappingTab.value && {
        label: 'Product Mapping',
        icon: ['fak', 'product-type'],
        tab: DataSourceSettingsTabType.PRODUCT_MAPPING,
      },
    ].filter(Boolean) as ModalSidebarItem<DataSourceSettingsTabType>[],
);
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.c-datasource-settings-modal__content {
  @apply flex flex-col p-24;
}
</style>

<style lang="pcss">
@reference "../../../css/theme.css";
.c-datasource-settings-modal__field-presets-footer
  .o-bitts-modal__footer-no-secondary-button {
  @apply !border-t !border-solid !border-neutral-border !border-r-0 !border-b-0 !border-l-0 !pt-16 !pb-16;
}

.c-datasource-settings-modal__partner-mapping-footer .o-bitts-modal__footer,
.c-datasource-settings-modal__product-mapping-footer .o-bitts-modal__footer {
  @apply !border-t !border-solid !border-neutral-border;
}
</style>
